import { useEffect, useState } from "react";
import { Settings2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyCredentials = {
  client_id: "",
  client_secret: "",
  subscription_id: "",
  username: "",
};

const saveErrorMessage = (error) => {
  const detail = error.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const fields = detail
      .map((item) => item?.loc?.[item.loc.length - 1])
      .filter(Boolean)
      .map((field) => String(field).replaceAll("_", " "));
    return fields.length
      ? `Please provide valid ${fields.join(", ")}.`
      : "Please complete all required GSTN configuration fields.";
  }
  return "Unable to save GSTN configuration";
};

export default function GSTNSetupDialog({ companyCode, onSaved }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [config, setConfig] = useState({
    company_code: companyCode,
    gstin: "",
    state_code: "",
    environment: "sandbox",
    ...emptyCredentials,
  });

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/settings/gst-config/${companyCode}`);
        setConfigured(data.configured);
        setConfig({
          company_code: companyCode,
          gstin: data.gstin || "",
          state_code: data.state_code || "",
          environment: data.environment || "sandbox",
          ...emptyCredentials,
        });
      } catch {
        setConfigured(false);
        setConfig({
          company_code: companyCode,
          gstin: "",
          state_code: "",
          environment: "sandbox",
          ...emptyCredentials,
        });
      }
    };

    load();
  }, [companyCode]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/gst-config`, config);
      toast.success("GSTN configuration saved securely");
      setConfigured(true);
      setOpen(false);
      setConfig((current) => ({ ...current, ...emptyCredentials }));
      onSaved?.();
    } catch (error) {
      toast.error(saveErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} className="h-9" data-testid="btn-gstn-setup">
        <Settings2 className="w-4 h-4 mr-2" />
        {configured ? "Update GSTN Setup" : "GSTN Setup"}
      </Button>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>GSTN Integration Setup</DialogTitle>
          <DialogDescription>
            Credentials are encrypted in the backend and are never displayed after saving.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2">
          <Input value={config.company_code} onChange={(event) => setConfig({ ...config, company_code: event.target.value })} placeholder="Company code" />
          <Input value={config.gstin} onChange={(event) => setConfig({ ...config, gstin: event.target.value.toUpperCase() })} placeholder="GSTIN" maxLength={15} />
          <Input value={config.state_code} onChange={(event) => setConfig({ ...config, state_code: event.target.value })} placeholder="State code, e.g. 27" maxLength={2} />
          <Select value={config.environment} onValueChange={(environment) => setConfig({ ...config, environment })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="sandbox">Sandbox</SelectItem>
              <SelectItem value="production">Production</SelectItem>
            </SelectContent>
          </Select>
          <Input value={config.username} onChange={(event) => setConfig({ ...config, username: event.target.value })} placeholder="Exact GST username" />
          <Input value={config.client_id} onChange={(event) => setConfig({ ...config, client_id: event.target.value })} placeholder="Exact GST client ID" />
          <Input type="password" value={config.client_secret} onChange={(event) => setConfig({ ...config, client_secret: event.target.value })} placeholder="Exact GST client secret" />
          <Input className="sm:col-span-2" value={config.subscription_id} onChange={(event) => setConfig({ ...config, subscription_id: event.target.value })} placeholder="Exact GST subscription ID" />
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving}>
            {saving ? "Saving..." : "Save secure configuration"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
